import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import { LoadingSection } from 'components';
import { LineBarChart } from 'dashboard409a/components';
import { compactFormatterNumber, formatMD } from 'dashboard409a/utilities';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { IValuationChartData } from './types';

interface EnterpriseValueChartProps {
  data: IValuationChartData[] | null;
  isLoading: boolean;
}

// Figma gradient for the Valuation section bars.
const BAR_TOP = '#037DE8';
const BAR_BASE = '#004A8B';

// Each bar runs the full light-to-dark ramp over its own height.
const barGradient = (context: ScriptableContext<'bar'>): CanvasGradient | string => {
  const { ctx, chartArea, scales } = context.chart;
  const { y: value } = context.parsed ?? {};
  if (!chartArea || !scales.y || typeof value !== 'number') {
    return BAR_BASE;
  }
  const top = scales.y.getPixelForValue(value);
  if (top >= chartArea.bottom) {
    return BAR_BASE;
  }
  const gradient = ctx.createLinearGradient(0, top, 0, chartArea.bottom);
  gradient.addColorStop(0, BAR_TOP);
  gradient.addColorStop(1, BAR_BASE);
  return gradient;
};

// Hover and tooltip behavior are stock Chart.js — only the visuals styled.
const OPTIONS: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#FFFFFF',
      titleColor: '#1F2937',
      titleFont: { weight: 'bold', size: 13 },
      bodyColor: '#4B5563',
      borderColor: 'rgba(0, 0, 0, 0.08)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 10,
      displayColors: false,
      callbacks: {
        label: context => formatCurrency({ value: context.parsed.y }),
      },
    },
  },
  scales: {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: '#9CA3AF' },
    },
    y: {
      border: { display: false, dash: [4, 4] },
      grid: { color: '#E5E7EB' },
      ticks: {
        color: '#9CA3AF',
        maxTicksLimit: 6,
        callback: value => `$${compactFormatterNumber.format(Number(value))}`,
      },
    },
  },
};

// One bar per historical valuation: the opinion's weighted market enterprise
// value at each measurement date, oldest to newest.
const EnterpriseValueChart: FC<EnterpriseValueChartProps> = ({ data, isLoading }) => {
  const chartData: ChartData<'bar'> = useMemo(() => {
    const sorted = [...(data ?? [])].sort(
      (a, b) =>
        new Date(a.report_date.measurement_date.date).getTime() - new Date(b.report_date.measurement_date.date).getTime()
    );
    return {
      labels: sorted.map(item => formatMD({ date: item.report_date.measurement_date.date, format: 'MM/DD/YY' })),
      datasets: [
        {
          data: sorted.map(item => +item.weighted_market_enterprise_value),
          backgroundColor: barGradient,
          borderRadius: 4,
          maxBarThickness: 48,
        },
      ],
    };
  }, [data]);

  return (
    <Box sx={{ height: 320 }}>
      {isLoading ? <LoadingSection /> : <LineBarChart type="bar" data={chartData} options={OPTIONS} />}
    </Box>
  );
};

export default EnterpriseValueChart;
